import React, { useEffect, useState } from 'react'
import { HelpDrawer } from '../HelpDrawerV2/HelpDrawer'
import { OnboardingHeaderTitleBar } from '../OnboardingHeaderTitleBar'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import { useStyles } from './TermsAndConditions.styles'

import {
  BoxV2,
  BoxV4,
  ButtonV2,
  CheckBoxFieldv2,
  ModalConfirmV2,
  ModalSizeVariants,
  TextInputV4,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShop, faExternalLink } from '@fortawesome/pro-regular-svg-icons'
import { Form, Formik, FormikHandlers } from 'formik'
import { FooterV2 } from '../FooterV2/FooterV2'
import { CompanyIndustrySelect } from './CompanyIndustrySelect'
import { CompanyInfoContinueButton } from './CompanyInfoContinueButton'
import { companyInfoSchema } from './companyInfoSchema'
import { CloudTypeEnum } from '../../types'
import { LabraAiAwsIntegrationBadge } from './LabraAiAwsIntegrationBadge'
import Disable from '../../images/Artboard 1 1.svg'
import { noop } from 'lodash'
import { ProductType } from './TermsAndConditions'
import { CloudSettingsHelpSections } from '../CloudSettingsHelpSections/CloudSettingsHelpSections'
import { flyoutCompanyDetailsFaq } from './flyoutCompanyDetailsFaq'
import { openSidePanelContentWidth } from './openSidePanelContentWidth'

export type CompanyInfo = {
  companyName: string
  partnershipTier: string | null
  industry: string
  websiteURL: string
  partnershipType: string | string[]
  isIsv: boolean
  accelerateProgram: boolean
  isRevenueRecognitionProgram: boolean
  acceptedTermsAndConditions: boolean
}

type TermsAndConditionsUIProps = {
  companyInfo: CompanyInfo
  productType?: ProductType
  onSubmit: (values: CompanyInfo) => void
}

export const TermsAndConditionsComponentUI: React.FC<
  TermsAndConditionsUIProps
> = ({ companyInfo, productType = ProductType.OPPSYNC, onSubmit }) => {
  const { classes } = useStyles()

  const [open, setOpen] = useState(false)

  const handleCheckboxChange =
    (handleChange: FormikHandlers['handleChange']) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange({ ...e, value: e.target.checked })
    }

  useEffect(() => {
    if (
      !companyInfo.websiteURL &&
      !companyInfo.industry &&
      productType !== ProductType.FLYOUT
    ) {
      setOpen(true)
    }
  }, [])

  return (
    <>
      <OnboardingHeaderTitleBar
        title={'Welcome to Labra! 👋'}
        cloudType={CloudTypeEnum.AWS}
      />
      {productType === ProductType.LABRA_AI && (
        <LabraAiAwsIntegrationBadge className={classes.labraAiAwsBadge} />
      )}
      <Formik
        initialValues={{
          ...companyInfo,
          partnershipType: Array.isArray(companyInfo.partnershipType)
            ? companyInfo.partnershipType
            : companyInfo.partnershipType
              ? [companyInfo.partnershipType]
              : [],
        }}
        validateOnMount={true}
        validationSchema={companyInfoSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          handleReset,
          setFieldValue,
        }) => {
          const handlePartnershipCheckbox = (
            e: React.ChangeEvent<HTMLInputElement>,
            partnerType: string
          ) => {
            const isChecked = e.target.checked
            handleChange({ ...e, value: isChecked })
            const currentTypes = [...(values.partnershipType as string[])]
            if (isChecked && !currentTypes.includes(partnerType)) {
              setFieldValue('partnershipType', [...currentTypes, partnerType])
            } else if (!isChecked && currentTypes.includes(partnerType)) {
              setFieldValue(
                'partnershipType',
                currentTypes.filter(t => t !== partnerType)
              )
            }
          }

          return (
            <Form onSubmit={handleSubmit} onReset={handleReset}>
              <div
                data-testid="company-information-page"
                className={classes.paperContainer}
              >
                <div className={classes.section}>
                  <BoxV4
                    heading={'Company details'}
                    headingIcon={
                      <FontAwesomeIcon
                        icon={faShop}
                        style={{ width: '20px', height: '17px' }}
                      />
                    }
                    secondaryActions={
                      productType === ProductType.FLYOUT ? (
                        <CloudSettingsHelpSections
                            content={flyoutCompanyDetailsFaq}
                        />
                      ) : (
                        <HelpDrawer
                          sectionName="oppSyncCompanyDetails"
                          headerTitle="Help"
                        />
                      )
                    }
                  >
                    <Grid container spacing={3} width={'100%'}>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 4,
                          lg: 4,
                          xl: 4,
                        }}
                      >
                        <TextInputV4
                          keyName="companyName"
                          title="Company name"
                          type="text"
                          placeHolder=""
                          value={values.companyName}
                          isError={
                            !!errors?.companyName && !!touched.companyName
                          }
                          onChange={handleChange}
                          isReadOnly={false}
                          isRequired={false}
                          errorText={errors?.companyName || ''}
                          showSpinner={false}
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 4,
                          lg: 4,
                          xl: 4,
                        }}
                      >
                        <TextInputV4
                          keyName="websiteURL"
                          title="Website"
                          type="text"
                          placeHolder=""
                          value={values.websiteURL}
                          isError={
                            !!errors?.websiteURL && !!touched?.websiteURL
                          }
                          onChange={handleChange}
                          isReadOnly={false}
                          isRequired={false}
                          errorText={errors?.websiteURL || ''}
                          showSpinner={false}
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                          md: 4,
                          lg: 4,
                          xl: 4,
                        }}
                      >
                        <CompanyIndustrySelect
                          industry={values.industry}
                          onIndustryChange={nextIndustry => {
                            void setFieldValue('industry', nextIndustry)
                          }}
                          onBlur={handleBlur}
                          isError={!!errors?.industry && !!touched?.industry}
                          errorText={errors?.industry || ''}
                        />
                      </Grid>
                    </Grid>
                    {productType === ProductType.CLOUDFAAS && (
                      <div className={classes.checkboxContainer}>
                        <div className={classes.box}>
                          <CheckBoxFieldv2
                            title={
                              <div className={classes.checkbox}>
                                <Typography className={classes.checkboxLabel}>
                                  Independent Software Vendor (ISV)
                                </Typography>
                                <Typography
                                  className={classes.checkboxSupportText}
                                >
                                  Your product(s) can be sold directly to
                                  customers or through third-party channels
                                </Typography>
                              </div>
                            }
                            keyName="isIsv"
                            onChangeBox={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              handlePartnershipCheckbox(e, 'TECHNOLOGY_PARTNER')
                            }
                            value={(
                              values.partnershipType as string[]
                            ).includes('TECHNOLOGY_PARTNER')}
                          />
                        </div>
                        <div className={classes.box}>
                          <CheckBoxFieldv2
                            title={
                              <div className={classes.checkbox}>
                                <Typography className={classes.checkboxLabel}>
                                  Channel Partner (CP)
                                </Typography>
                                <Typography
                                  className={classes.checkboxSupportText}
                                >
                                  You partner with ISVs and sell software on
                                  behalf of an ISV.
                                </Typography>
                              </div>
                            }
                            keyName="isRevenueRecognitionProgram"
                            onChangeBox={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                              handlePartnershipCheckbox(e, 'CONSULTING_PARTNER')
                            }
                            value={(
                              values.partnershipType as string[]
                            ).includes('CONSULTING_PARTNER')}
                          />
                        </div>
                      </div>
                    )}
                  </BoxV4>
                  <ModalConfirmV2
                    variant={ModalSizeVariants.medium}
                    onlyCloseModal={true}
                    hideCancelButton={true}
                    open={open}
                    hideAcceptButton={true}
                    onAccept={noop}
                    onDecline={noop}
                    title=""
                    hideCancelIcon={true}
                    content={
                      <div>
                        <Grid container data-testid="disable-page">
                          <Grid
                            size={{
                              xs: 12,
                              sm: 12,
                              md: 12,
                              lg: 12,
                            }}
                          >
                            <div className={classes.imgRoot}>
                              <img
                                src={Disable}
                                alt="Stock-Image"
                                className={classes.disableImage}
                                data-testid="stock-image"
                              />
                            </div>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              sm: 12,
                              md: 12,
                              lg: 12,
                            }}
                          >
                            <Typography className={classes.modalHeader}>
                              Welcome to Labra!
                            </Typography>
                          </Grid>

                          <Grid
                            size={{
                              xs: 12,
                              sm: 12,
                              md: 12,
                              lg: 12,
                            }}
                          >
                            <div className={classes.modalRoot}>
                              <div>
                                Let’s get things set up with our smart
                                onboarding process designed to get you up and
                                running in no time. It’s quick, straightforward,
                                and will ensure you have everything you need for
                                a smooth start.
                              </div>
                            </div>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              sm: 12,
                              md: 12,
                              lg: 12,
                            }}
                          >
                            <Typography className={classes.modalBottomInfo}>
                              P.S. This won’t take long, and you’ll be ready to
                              explore in just a few minutes!
                            </Typography>
                          </Grid>

                          <Grid
                            size={{
                              xs: 12,
                              sm: 12,
                              md: 12,
                              lg: 12,
                            }}
                          >
                            <div className={classes.button}>
                              <ButtonV2 onClick={() => setOpen(false)}>
                                Let's go
                              </ButtonV2>
                            </div>
                          </Grid>
                        </Grid>
                      </div>
                    }
                  />
                </div>

                <div className={classes.section}>
                  <BoxV2>
                    <div className={classes.checkTermsAndConditions}>
                      <CheckBoxFieldv2
                        keyName="acceptedTermsAndConditions"
                        title={
                          <Typography className={classes.termsAndConditions}>
                            I have read and agreed to the{' '}
                            <a
                              href="https://www.labra.io/terms-and-conditions/"
                              target="_blank"
                              className={classes.url}
                            >
                              Labra Terms and Conditions &nbsp;&nbsp;
                              <FontAwesomeIcon
                                icon={faExternalLink}
                                style={{ width: '14px', height: '14px' }}
                              />
                            </a>
                          </Typography>
                        }
                        value={values.acceptedTermsAndConditions || false}
                        onChangeBox={handleCheckboxChange(handleChange)}
                      />
                      <div className={classes.errorMessage}>
                        {errors.acceptedTermsAndConditions &&
                        touched.acceptedTermsAndConditions
                          ? 'You must agree to the terms and conditions before continuing.'
                          : ''}
                      </div>
                    </div>
                  </BoxV2>
                </div>
              </div>
              <FooterV2
                customClass={classes.footerCustomClass}
                width={openSidePanelContentWidth}
                actions={
                  <div className={classes.actionsButton}>
                    <CompanyInfoContinueButton />
                  </div>
                }
              />
            </Form>
          )
        }}
      </Formik>
    </>
  )
}
